import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ArticlePickerService } from '../../../../core/catalog';
import { Article, ArticlePickerAttribute, ArticlePickerAttributeOption, ArticlePickerEvent } from '../../../../core/model';
import { shallowEqualArrays } from '../../../../core/util';

@Component({
  selector: 'py-picker-attribute',
  templateUrl: './picker-attribute.component.html',
  styleUrls: ['./picker-attribute.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PickerAttributeComponent implements OnInit, OnDestroy, OnChanges {
  @Input() articles: Article[] = [];
  @Input() selectedAttributes: ArticlePickerEvent[];
  @Input() attribute: ArticlePickerAttribute;
  @Input() resetable: boolean;
  @Input() loading: boolean;

  @Output() resetValue = new EventEmitter<void>();
  @Output() updateSelectedAttribute = new EventEmitter<ArticlePickerEvent>();

  attributeOptions$ = new BehaviorSubject<ArticlePickerAttributeOption[]>([]);

  form = new UntypedFormGroup({
    value: new UntypedFormControl(''),
  });

  subscriptions = new Subscription();

  constructor(private articlePickerService: ArticlePickerService) {}

  get autoSelected() {
    return this.autoSelectedAttributes.find((sa) => sa === this.attribute?.code);
  }

  get autoSelectedAttributes(): string[] {
    return this.selectedAttributes?.filter((sa) => sa.auto).map((sa) => sa.attributeCode) || [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    const selectedAttributes = changes['selectedAttributes'];
    const attribute = changes['attribute']?.currentValue || this.attribute;
    const articles = changes['articles']?.currentValue || this.articles;

    if (
      attribute &&
      articles &&
      selectedAttributes &&
      !shallowEqualArrays(selectedAttributes.previousValue, selectedAttributes.currentValue)
    ) {
      let attributes = selectedAttributes?.currentValue || [];

      if (changes['articles'] && shallowEqualArrays(changes['articles']?.previousValue, changes['articles']?.currentValue)) {
        attributes = [];
      }

      const currentSelectedAttribute = attributes.find((sa) => sa.attributeCode === attribute?.code) || {
        attributeCode: attribute?.code,
        value: null,
        auto: false,
      };

      this.setOptions(
        currentSelectedAttribute,
        attribute.options || [],
        attributes,
        articles || [],
        selectedAttributes.firstChange
      );
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.form.get('value').valueChanges.subscribe((value) => {
        this.updateSelectedAttribute.emit({ attributeCode: this.attribute.code, value, auto: false });
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setOptions(
    selectedAttribute: ArticlePickerEvent,
    options: ArticlePickerAttributeOption[],
    selectedAttributes: ArticlePickerEvent[],
    articles: Article[],
    _firstChange: boolean
  ) {
    let attributeOptions = [...(options || []).map((o) => ({ ...o }))];

    const autoSelectedAttributes = selectedAttributes?.filter((sa) => sa.auto).map((sa) => sa.attributeCode) || [];
    const auto = autoSelectedAttributes.find((sa) => sa === selectedAttribute.attributeCode);
    const formValue = this.form.get('value');

    if (selectedAttributes.length > 0 && selectedAttributes.length === autoSelectedAttributes.length) {
      formValue.setValue(selectedAttribute?.value || null, { emitEvent: false });
      this.attributeOptions$.next(attributeOptions);
      return;
    }

    const limitedSelectedAttributes = auto
      ? selectedAttributes
      : selectedAttributes.filter(
          (sa) => ![...autoSelectedAttributes, selectedAttribute.attributeCode].includes(sa.attributeCode)
        );

    attributeOptions = this.articlePickerService.disableOptions(
      attributeOptions,
      selectedAttribute.attributeCode,
      limitedSelectedAttributes,
      articles
    );

    const availableOptions = attributeOptions.filter((ao) => !ao.disabled);

    if (
      availableOptions.length === 1 &&
      (formValue.value !== availableOptions[0].value || auto) &&
      attributeOptions?.length > 1
    ) {
      if (formValue.value === availableOptions[0].value) {
        return;
      }
      this.updateSelectedAttribute.emit({
        attributeCode: this.attribute.code,
        value: availableOptions[0].value,
        auto: true,
      });
      formValue.setValue(availableOptions[0].value, { emitEvent: false });
    } else if (attributeOptions?.length === 1) {
      this.updateSelectedAttribute.emit({
        attributeCode: this.attribute.code,
        value: attributeOptions[0].value,
        auto: false,
      });
      formValue.setValue(attributeOptions[0].value || null, { emitEvent: false });
    } else if (availableOptions.length !== 1 && auto) {
      this.updateSelectedAttribute.emit({
        attributeCode: this.attribute.code,
        value: null,
        auto: false,
      });
      formValue.setValue(null, { emitEvent: false });
    } else {
      formValue.setValue(selectedAttribute?.value || null, { emitEvent: false });
    }

    this.attributeOptions$.next(attributeOptions);
  }
}
